import { useQuery } from '@tanstack/react-query';
import { apiGetPublic } from '@/lib/api';

export type PublicAccountSettings = {
  preferred_image_ratio: 'landscape' | 'portrait';
  default_country_code: string | null;
  /** Whether this account gets the (brand-specific) social card templates. */
  social_cards_enabled: boolean;
};

/**
 * Account display prefs for the public invite/show forms, fetched via the
 * token-scoped public API. Unlike usePreferredImageRatio / useDefaultCountry
 * (which call the DAL's getAccount directly), this works on the anonymous
 * invite/show pages: the browser client can't read the accounts row once RLS is
 * on, so the server resolves the account from the flow token instead.
 *
 * `flow` picks the token type — 'artist' uses the invite token, 'show' the stub
 * token. Falls back to sensible defaults while loading or on error.
 */
export function usePublicAccountSettings(
  flow: 'artist' | 'show',
  token: string | null | undefined,
): PublicAccountSettings {
  const { data } = useQuery({
    queryKey: ['public-account-settings', flow, token],
    queryFn: () =>
      apiGetPublic<PublicAccountSettings>(`/${flow}/account-settings`, {
        token: token ?? undefined,
      }),
    enabled: Boolean(token),
    staleTime: 5 * 60 * 1000,
  });

  return {
    preferred_image_ratio: data?.preferred_image_ratio ?? 'landscape',
    default_country_code: data?.default_country_code ?? null,
    social_cards_enabled: data?.social_cards_enabled ?? false,
  };
}
